import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, XPBar } from "@/components/common";
import { RewardAdDialog } from "@/components/RewardAd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Pencil, UserPlus, UserCheck, Ban, Gift, Rocket, Crown, ShieldCheck, MapPin, Camera, Loader2, Award } from "lucide-react";

export default function Profile() {
  const { uid } = useParams();
  const { user, applyUser } = useAuth();
  const navigate = useNavigate();
  const isSelf = !uid || uid === user?.id;
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  const load = async () => {
    if (isSelf) { setProfile(user); }
    else { try { const { data } = await api.get(`/users/${uid}`); setProfile(data); } catch { toast.error("User not found"); } }
  };
  useEffect(() => { load(); api.get("/badges").then((r) => setBadges(r.data)); }, [uid, user]);

  const follow = async () => {
    try { const { data } = await api.post(`/users/${uid}/follow`); setProfile((p) => ({ ...p, is_following: data.following, followers: p.followers + (data.following ? 1 : -1) })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const block = async () => {
    try { const { data } = await api.post(`/users/${uid}/block`); toast.success(data.blocked ? "User blocked" : "User unblocked"); setProfile((p) => ({ ...p, is_blocked: data.blocked })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const boost = async () => {
    try { const { data } = await api.post("/boost"); applyUser(data.user); toast.success("Profile boosted for 24h — you'll rank higher in search!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!profile) return <p className="text-center text-muted-foreground py-8">Loading profile…</p>;
  const bannerUrl = fileUrl(profile.banner);
  const badgeMap = Object.fromEntries(badges.map((b) => [b.id, b]));

  return (
    <div className="space-y-4">
      {/* Banner + avatar */}
      <div className="relative">
        <div className="h-36 rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-jixxeo-green/20 via-jixxeo-blue/20 to-jixxeo-purple/20">
          {bannerUrl && <img src={bannerUrl} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="absolute -bottom-8 left-5"><Avatar user={profile} size={80} /></div>
        {isSelf && (
          <Button onClick={() => setEditOpen(true)} data-testid="edit-profile-btn" size="sm" className="absolute top-3 right-3 rounded-full glass border border-white/20 h-9"><Pencil className="w-4 h-4 mr-1" />Edit</Button>
        )}
      </div>

      <div className="pt-8 px-1">
        <div className="flex items-center gap-2">
          <h2 className="font-head font-black text-2xl">{profile.name}</h2>
          {profile.elite && <span className="text-[10px] elite-shimmer text-white px-2 py-0.5 rounded-full font-bold">ELITE</span>}
          {profile.verified && <ShieldCheck className="w-5 h-5 text-jixxeo-green" />}
        </div>
        <p className="text-sm text-jixxeo-blue font-bold">@{profile.username} · {profile.rank}</p>
        {profile.city && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{profile.city}</p>}
        {profile.bio && <p className="text-sm text-white/80 mt-2">{profile.bio}</p>}
        {profile.socials && Object.values(profile.socials).some(Boolean) && (
          <div className="flex gap-3 mt-2 text-xs">
            {Object.entries(profile.socials).filter(([, v]) => v).map(([k, v]) => (
              <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-jixxeo-blue hover:underline capitalize" data-testid={`social-${k}`}>{k}</a>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3"><XPBar progress={profile.level_progress} /></div>

      {/* Actions */}
      {isSelf ? (
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={boost} data-testid="boost-btn" className="rounded-full bg-jixxeo-purple text-white font-bold hover:opacity-90"><Rocket className="w-4 h-4 mr-1" />Boost (200🪙)</Button>
          <Button onClick={() => navigate("/elite")} data-testid="go-elite-btn" className="rounded-full elite-shimmer text-white font-bold"><Crown className="w-4 h-4 mr-1" />Elite</Button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={follow} data-testid="follow-btn" className={`rounded-full font-bold ${profile.is_following ? "glass border border-white/10" : "bg-jixxeo-green text-black hover:bg-jixxeo-greenDark"}`}>
            {profile.is_following ? <><UserCheck className="w-4 h-4 mr-1" />Following</> : <><UserPlus className="w-4 h-4 mr-1" />Follow</>}
          </Button>
          <Button onClick={() => setTipOpen(true)} data-testid="tip-btn" className="rounded-full bg-jixxeo-gold text-black font-bold hover:bg-yellow-400"><Gift className="w-4 h-4 mr-1" />Tip</Button>
          <Button onClick={block} data-testid="block-btn" variant="ghost" className="rounded-full glass border border-white/10 text-jixxeo-danger"><Ban className="w-4 h-4 mr-1" />{profile.is_blocked ? "Unblock" : "Block"}</Button>
        </div>
      )}

      {/* Stats bento */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Level" value={profile.level} color="text-jixxeo-green" />
        <Stat label="Cleanups" value={profile.total_cleanups} color="text-white" />
        <Stat label="Collected" value={`${(profile.total_weight_g / 1000).toFixed(1)}kg`} color="text-jixxeo-blue" />
        <Stat label="Streak" value={`${profile.streak}d`} color="text-jixxeo-danger" />
        <Stat label="Followers" value={profile.followers} color="text-white" />
        <Stat label="Following" value={profile.following} color="text-white" />
        <Stat label="Coins" value={profile.coins} color="text-jixxeo-gold" />
        <Stat label="Verified" value={profile.verified_cleanups} color="text-jixxeo-green" />
      </div>

      {/* Badges */}
      <div>
        <div className="flex items-center gap-2 mb-3"><Award className="w-5 h-5 text-jixxeo-gold" /><h3 className="font-head font-extrabold text-lg">Badges & Achievements</h3></div>
        {profile.badges?.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {profile.badges.map((bid) => {
              const b = badgeMap[bid]; if (!b) return null;
              const Icon = Icons[b.icon.charAt(0).toUpperCase() + b.icon.slice(1).replace(/-(.)/g, (_, c) => c.toUpperCase())] || Award;
              const tierColor = b.tier === "gold" ? "text-jixxeo-gold neon-gold" : b.tier === "silver" ? "text-gray-300" : b.tier === "elite" ? "text-jixxeo-purple neon-purple" : "text-orange-400";
              return (
                <div key={bid} className="glass rounded-2xl p-3 flex flex-col items-center text-center" data-testid={`badge-${bid}`}>
                  <Icon className={`w-7 h-7 ${tierColor}`} />
                  <p className="text-[11px] font-bold mt-1.5">{b.name}</p>
                  <p className="text-[9px] text-muted-foreground">{b.desc}</p>
                </div>
              );
            })}
          </div>
        ) : <div className="glass rounded-2xl p-6 text-center text-muted-foreground text-sm">No badges yet. Keep cleaning to unlock them!</div>}
      </div>

      {isSelf && <EditDialog open={editOpen} setOpen={setEditOpen} user={user} applyUser={applyUser} />}
      {!isSelf && <TipDialog open={tipOpen} setOpen={setTipOpen} target={profile} applyUser={applyUser} />}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="glass rounded-2xl p-3">
      <p className={`font-display text-xl ${color}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function EditDialog({ open, setOpen, user, applyUser }) {
  const [form, setForm] = useState({ name: user.name, bio: user.bio || "", city: user.city || "", socials: user.socials || {} });
  const [avatar, setAvatar] = useState(user.avatar);
  const [banner, setBanner] = useState(user.banner);
  const [busy, setBusy] = useState(false);
  const avatarRef = useRef(); const bannerRef = useRef();

  const upload = async (file, setter) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setter(data.path); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", { ...form, avatar, banner });
      applyUser(data); toast.success("Profile updated"); setOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-3">
            <button onClick={() => avatarRef.current.click()} className="relative" data-testid="upload-avatar">
              <Avatar user={{ ...user, avatar }} size={64} />
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full"><Camera className="w-5 h-5" /></span>
            </button>
            <button onClick={() => bannerRef.current.click()} className="flex-1 h-16 rounded-xl border border-white/10 flex items-center justify-center bg-white/5 relative overflow-hidden" data-testid="upload-banner">
              {fileUrl(banner) && <img src={fileUrl(banner)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <span className="relative flex items-center gap-1 text-xs text-muted-foreground"><Camera className="w-4 h-4" />Banner</span>
            </button>
            <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], setAvatar)} />
            <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], setBanner)} />
          </div>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="edit-name" className="bg-white/5 border-white/10 rounded-xl" />
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio" data-testid="edit-bio" className="bg-white/5 border-white/10 rounded-xl" />
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" data-testid="edit-city" className="bg-white/5 border-white/10 rounded-xl" />
          <Input value={form.socials.instagram || ""} onChange={(e) => setForm({ ...form, socials: { ...form.socials, instagram: e.target.value } })} placeholder="Instagram URL" className="bg-white/5 border-white/10 rounded-xl" />
          <Input value={form.socials.twitter || ""} onChange={(e) => setForm({ ...form, socials: { ...form.socials, twitter: e.target.value } })} placeholder="Twitter/X URL" className="bg-white/5 border-white/10 rounded-xl" />
          <Button onClick={save} disabled={busy} data-testid="save-profile" className="w-full rounded-full bg-jixxeo-green text-black font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TipDialog({ open, setOpen, target, applyUser }) {
  const [method, setMethod] = useState("coins");
  const [amount, setAmount] = useState("");
  const [ad, setAd] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tips", { to_user: target.id, method, amount: parseFloat(amount || 0) });
      applyUser(data.user); toast.success(`Tip sent to ${target.name}!`); setOpen(false); setAmount("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submit = () => {
    if (method === "ad") { setAd(true); return; }
    send();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass border-white/10">
        {ad && <RewardAdDialog open onClose={() => setAd(false)} onComplete={() => { setAd(false); send(); }} label="Ad Tip" />}
        <DialogHeader><DialogTitle>Tip {target.name}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {["coins", "cash", "ad"].map((m) => (
            <button key={m} onClick={() => setMethod(m)} data-testid={`tip-method-${m}`} className={`flex-1 py-2 rounded-full text-sm font-bold capitalize ${method === m ? "bg-jixxeo-green text-black" : "glass text-muted-foreground"}`}>{m}</button>
          ))}
        </div>
        {method !== "ad" ? (
          <Input type="number" placeholder={method === "coins" ? "Coins to tip" : "Cash amount"} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tip-amount" className="bg-white/5 border-white/10 rounded-xl h-12" />
        ) : <p className="text-xs text-muted-foreground">Watch a short ad to gift 15 coins to {target.name} — costs you nothing.</p>}
        <Button onClick={submit} disabled={busy} data-testid="tip-send" className="rounded-full bg-jixxeo-gold text-black font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Tip"}</Button>
      </DialogContent>
    </Dialog>
  );
}
